import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import {
  fetchWeather,
  fetchListWeather,
  WeatherItem,
  ListWeatherItem,
} from "api/Weather";

const DAY_NAMES = ["CHỦ NHẬT", "THỨ 2", "THỨ 3", "THỨ 4", "THỨ 5", "THỨ 6", "THỨ 7"];

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function useClock() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const seconds = `:${pad(now.getSeconds())}`;
  const session = now.getHours() < 12 ? "SÁNG" : now.getHours() < 18 ? "CHIỀU" : "TỐI";
  const dateLabel = `TP.HCM, ${session} ${DAY_NAMES[now.getDay()]}, ${pad(now.getDate())}/${pad(
    now.getMonth() + 1
  )}/${now.getFullYear()}`;

  const hour = now.getHours();

  const greeting =
    hour < 12
      ? "Good morning"
      : hour < 18
        ? "Good afternoon"
        : "Good evening";

  return { time, seconds, dateLabel, greeting };
}

export function Footer() {
  const [chatOpen, setChatOpen] = useState(false);
  const [weatherOpen, setWeatherOpen] = useState(false);
  const { time, seconds, dateLabel, greeting } = useClock();

  const [weathers, setWeather] = useState<WeatherItem>();
  const [forecast, setForecast] = useState<ListWeatherItem[]>([]);

  const getWeather = async () => {
    const data = await fetchWeather();
    setWeather(data);
  }

  const getForecast = async () => {
    const data = await fetchListWeather();

    // lấy mốc 12:00 mỗi ngày
    const forecast5Days = data.list
      .filter(item => item.dt_txt.endsWith("12:00:00"))
      .slice(0, 4);

    setForecast(forecast5Days);
  };

  useEffect(() => {
    getWeather()
    getForecast();
  }, [])

  return (
    <footer className="relative w-full z-40 pointer-events-none">
      {/* Desktop Layout */}
      <div className="hidden lg:flex absolute bottom-0 left-0 w-full p-6 items-end justify-between pointer-events-none">
        {/* Left: Clock & Weather */}
        <div className="flex flex-col space-y-4 pointer-events-auto">
          <div className="flex flex-col">
            <div className="flex items-baseline space-x-1">
              <span className="text-5xl font-light text-white">{time}</span>
              <span className="text-xl font-light text-gray-400">{seconds}</span>
            </div>
            <span className="text-xs text-gray-400 mt-1 uppercase tracking-wide">{dateLabel}</span>
          </div>

          <div className="flex space-x-2 mt-4 relative">
            <button
              type="button"
              onClick={() => setChatOpen((v) => !v)}
              aria-label="Toggle chat"
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg transition z-50"
            >
              <i className="fa-solid fa-message text-xs" />
            </button>
            <button
              type="button"
              aria-label="Tasks"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg transition relative"
            >
              <i className="fa-solid fa-check text-xs" />
              <span className="absolute -top-1 -right-1 bg-red-500 rounded-full w-3 h-3 border border-gray-900" />
            </button>

            <div className="ml-4 flex items-center text-[10px] text-gray-400 space-x-3 bg-gray-800 bg-opacity-40 rounded px-2 py-1">
              <span className="flex items-center">
                <img src={`https://openweathermap.org/img/wn/${weathers?.weather[0].icon}@2x.png`} alt="icon_weather" style={{ width: '20px' }} /> {weathers?.weather[0].main}
              </span>
              <span className="flex items-center">
                62 MPH <i className="fa-solid fa-arrow-down ml-1" />
              </span>
              {weathers && (
                <span className="flex items-center">
                  <i className="fa-solid fa-temperature-half mr-1" /> {Math.round(weathers.main.temp)}°
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Center: Greetings & Weather Icon */}
        <div className="flex flex-col items-center pointer-events-auto">
          <div className="flex items-center space-x-4 mb-2">
            <div className="relative w-12 h-12 flex items-center justify-center">
              <i className="fa-solid fa-sun text-yellow-400 text-2xl absolute -top-1 -left-1" />
              <i className="fa-solid fa-cloud text-blue-300 text-3xl relative z-10 opacity-90" />
            </div>
            <div className="flex flex-col">
              <h2 className="text-xl font-medium text-white">{greeting}, Admin</h2>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">THÔNG BÁO TOÀN CÔNG TY</span>
                <div className="flex space-x-1">
                  <button type="button" aria-label="Previous announcement" className="text-gray-500 hover:text-white">
                    <i className="fa-solid fa-chevron-left text-[8px]" />
                  </button>
                  <button type="button" aria-label="Next announcement" className="text-gray-500 hover:text-white">
                    <i className="fa-solid fa-chevron-right text-[8px]" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center text-[10px] text-gray-400 space-x-4 mt-2">
            <span className="flex items-center">
              <i className="fa-solid fa-droplet mr-1" /> {weathers?.rain?.["1h"]}%
            </span>
            <span className="flex items-center">
              <i className="fa-solid fa-sun mr-1" />  {weathers
                ? new Date(weathers.sys.sunrise * 1000).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit", }) : "--:--"}
            </span>
            <span className="flex items-center">
              <i className="fa-regular fa-sun mr-1" /> {weathers
                ? new Date(weathers.sys.sunset * 1000).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit", }) : "--:--"}
            </span>
          </div>
        </div>

        {/* Right: Weather & Calendar Widget */}
        {/* <div className="relative z-50 w-72 bg-gray-900/40 backdrop-blur-md border border-white/5 rounded-xl flex-col overflow-hidden pointer-events-auto shadow-2xl mr-4 mb-2 flex transition-all duration-300 hover:shadow-blue-900/20 hover:border-gray-600/50">
              <div className="bg-gray-800 bg-opacity-40 text-white px-4 py-3 flex items-center justify-between text-xs font-medium border-b border-gray-700 border-opacity-50">
                <div className="flex items-center space-x-2">
                  <i className="fa-solid fa-calendar-day text-blue-400" />
                  <span className="tracking-wide">Dự báo thời tiết</span>
                </div>
                <span className="text-[10px] text-gray-400 bg-gray-800/50 px-2 py-0.5 rounded-full">TP. Hồ Chí Minh</span>
              </div>

              <div className="p-4 flex flex-col space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <img src={`https://openweathermap.org/img/wn/${weathers?.weather[0].icon}@2x.png`} alt="icon_weather" />
                    <div className="flex flex-col">
                      {weathers && (
                        <div className="text-3xl font-light text-white">
                            {Math.round(weathers.main.temp)}°
                            <span className="text-lg text-gray-400">C</span>
                        </div>
                        )}
                      <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider">{ weathers?.weather[0].description }</div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 pt-4 border-t border-gray-700 border-opacity-50">
                  {forecast.map((day, index) => (
                    <div
                      key={day.dt}
                      className="flex flex-col items-center space-y-2 p-1.5 rounded-lg hover:bg-gray-700 hover:bg-opacity-40 transition cursor-pointer group"
                    >
                      <span className="text-[10px] text-gray-400 uppercase font-medium group-hover:text-white transition">
                       {index === 0
                        ? "Hôm nay"
                        : new Date(day.dt * 1000).toLocaleDateString("vi-VN", {
                            weekday: "short",
                          })}
                      </span>
                      <img src={`https://openweathermap.org/img/wn/${day.weather[0].icon}@2x.png`} alt="icon_weather" />
                      <span className="text-[11px] font-semibold text-white">{Math.round(day.main.temp)}°C</span>
                    </div>
                  ))}
                </div>
              </div>
            </div> */}
      </div>

      {/* Tablet Layout (md:flex lg:hidden) */}
      <div className="hidden md:flex lg:hidden absolute bottom-0 left-0 right-0 p-4 items-end justify-between pointer-events-auto gap-4">
        {/* Left: Clock */}
        <div className="flex flex-col space-y-2 flex-shrink-0">
          <div className="flex items-baseline space-x-1">
            <span className="text-3xl font-light text-white">{time}</span>
            <span className="text-sm font-light text-gray-400">{seconds}</span>
          </div>
          <span className="text-xs text-gray-400 uppercase tracking-wide line-clamp-1">{dateLabel}</span>
        </div>

        {/* Center Actions */}
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setChatOpen((v) => !v)}
            aria-label="Toggle chat"
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg transition"
          >
            <i className="fa-solid fa-message text-xs" />
          </button>
          <button
            type="button"
            aria-label="Tasks"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg transition relative"
          >
            <i className="fa-solid fa-check text-xs" />
            <span className="absolute -top-1 -right-1 bg-red-500 rounded-full w-2.5 h-2.5 border border-gray-900" />
          </button>
        </div>

        {/* Right: Weather Toggle */}
        <button
          type="button"
          onClick={() => setWeatherOpen(!weatherOpen)}
          className="flex items-center text-[10px] text-gray-400 space-x-1 bg-gray-800 bg-opacity-40 rounded px-2 py-1 hover:bg-opacity-60 transition flex-shrink-0"
        >
          <img src={`https://openweathermap.org/img/wn/${weathers?.weather[0].icon}@2x.png`} alt="icon_weather" style={{ width: '16px' }} />
          {weathers && <span>{Math.round(weathers.main.temp)}°</span>}
        </button>
      </div>

      {/* Mobile Layout - Compact */}
      <div className="flex md:hidden absolute bottom-0 left-0 right-0 p-3 items-end justify-between pointer-events-auto gap-2">
        {/* Left: Clock Compact */}
        <div className="flex flex-col flex-shrink-0">
          <div className="flex items-baseline space-x-0.5">
            <span className="text-2xl font-light text-white">{time}</span>
            <span className="text-xs font-light text-gray-400">{seconds}</span>
          </div>
          <span className="text-[10px] text-gray-400 uppercase tracking-widest line-clamp-1">TP.HCM</span>
        </div>

        {/* Center: Quick Actions */}
        <div className="flex space-x-1.5">
          <button
            type="button"
            onClick={() => setChatOpen((v) => !v)}
            aria-label="Toggle chat"
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg transition"
          >
            <i className="fa-solid fa-message text-[10px]" />
          </button>
          <button
            type="button"
            aria-label="Tasks"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg transition relative"
          >
            <i className="fa-solid fa-check text-[10px]" />
            <span className="absolute -top-1 -right-1 bg-red-500 rounded-full w-2 h-2 border border-gray-900" />
          </button>
        </div>

        {/* Right: Mini Weather */}
        <button
          type="button"
          onClick={() => setWeatherOpen(!weatherOpen)}
          className="flex items-center text-[9px] text-gray-400 bg-gray-800 bg-opacity-40 rounded px-1.5 py-0.5 hover:bg-opacity-60 transition flex-shrink-0"
        >
          <img src={`https://openweathermap.org/img/wn/${weathers?.weather[0].icon}@2x.png`} alt="icon_weather" style={{ width: '12px' }} />
          {weathers && <span>{Math.round(weathers.main.temp)}°</span>}
        </button>
      </div>

      {/* Weather Modal - Visible on smaller screens */}
      {weatherOpen && (
        <div className="fixed inset-0 lg:hidden z-50 bg-black/50 flex items-end pointer-events-auto">
          <div className="w-full bg-gray-900/95 backdrop-blur-xl rounded-t-2xl p-4 max-h-[50vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-white">Dự báo thời tiết</h3>
              <button
                type="button"
                onClick={() => setWeatherOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="flex flex-col space-y-4">
              <div className="flex items-center space-x-3">
                <img src={`https://openweathermap.org/img/wn/${weathers?.weather[0].icon}@2x.png`} alt="icon_weather" />
                <div>
                  {weathers && (
                    <div className="text-2xl font-light text-white">
                      {Math.round(weathers.main.temp)}°C
                    </div>
                  )}
                  <div className="text-xs text-gray-400 uppercase">{weathers?.weather[0].description}</div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2 pt-4 border-t border-gray-700">
                {forecast.map((day, index) => (
                  <div
                    key={day.dt}
                    className="flex flex-col items-center space-y-1 p-2 rounded-lg bg-gray-800/50"
                  >
                    <span className="text-[9px] text-gray-400 uppercase font-medium">
                      {index === 0
                        ? "Hôm nay"
                        : new Date(day.dt * 1000).toLocaleDateString("vi-VN", {
                          weekday: "short",
                        })}
                    </span>
                    <img src={`https://openweathermap.org/img/wn/${day.weather[0].icon}@2x.png`} alt="icon_weather" style={{ width: '24px' }} />
                    <span className="text-xs font-semibold text-white">{Math.round(day.main.temp)}°</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chatbot Window - Responsive */}
      <div
        className={`
              chat-window fixed
              bottom-16 sm:bottom-20 md:bottom-24 lg:bottom-[110px]
              left-3 sm:left-4 lg:left-6
              right-3 sm:right-4 lg:right-auto
              w-auto sm:w-80 md:w-[360px] lg:w-80
              max-w-[calc(100vw-24px)] sm:max-w-80
              h-[320px] sm:h-[350px] md:h-[400px] lg:h-[28rem]
              bg-gray-900/95 backdrop-blur-xl rounded-2xl
              border border-gray-700 shadow-2xl
              flex flex-col transform origin-bottom-left
              pointer-events-none z-50
              transition-all duration-300
              ${chatOpen ? 'scale-100 opacity-100 pointer-events-auto' : 'scale-75 sm:scale-0 opacity-0'}
            `}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 sm:p-4 rounded-t-2xl flex items-center justify-between shadow-md relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-white/10" />
          <div className="flex items-center space-x-3 relative z-10 min-w-0">
            <div className="w-7 sm:w-8 h-7 sm:h-8 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm shadow-inner flex-shrink-0">
              <i className="fa-solid fa-robot text-white text-xs sm:text-sm" />
            </div>
            <div className="min-w-0">
              <h3 className="text-white font-medium text-xs sm:text-sm drop-shadow-sm truncate">Khothi AI Assistant</h3>
              <p className="text-blue-100 text-[9px] sm:text-[10px] flex items-center">
                <span className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full bg-green-400 mr-1 shadow-[0_0_5px_#4ade80] flex-shrink-0" />
                <span className="truncate">Đang trực tuyến</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setChatOpen(false)}
            aria-label="Close chat"
            className="text-white/70 hover:text-white transition-colors relative z-10 p-1 w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/20 flex-shrink-0"
          >
            <i className="fa-solid fa-xmark text-sm" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 flex flex-col space-y-3 sm:space-y-4 custom-scrollbar">
          <div className="flex justify-center">
            <span className="text-[9px] sm:text-[10px] text-gray-500 bg-gray-800/50 px-2 py-0.5 rounded-full">Hôm nay</span>
          </div>

          <div className="flex items-start space-x-2">
            <div className="w-6 sm:w-7 h-6 sm:h-7 rounded-full bg-blue-500/20 flex-shrink-0 flex items-center justify-center mt-1 border border-blue-500/30">
              <i className="fa-solid fa-robot text-blue-400 text-xs" />
            </div>
            <div className="bg-gray-800 rounded-2xl rounded-tl-sm px-3 sm:px-4 py-2 max-w-[85%] border border-gray-700/50 shadow-sm">
              <p className="text-xs sm:text-sm text-gray-200">
                Xin chào Ngọc! 👋 Tôi là trợ lý ảo của Khơ Thị. Tôi có thể giúp gì cho bạn hôm nay?
              </p>
              <span className="text-[8px] sm:text-[9px] text-gray-500 mt-1 block text-right">11:58</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-1 sm:gap-2 ml-8">
            <button
              type="button"
              className="text-[9px] sm:text-xs bg-gray-800/50 hover:bg-blue-600 hover:border-blue-500 border border-gray-700 text-gray-300 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full transition-all duration-200 shadow-sm whitespace-nowrap"
            >
              📅 Lịch họp
            </button>
            <button
              type="button"
              className="text-[9px] sm:text-xs bg-gray-800/50 hover:bg-blue-600 hover:border-blue-500 border border-gray-700 text-gray-300 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full transition-all duration-200 shadow-sm whitespace-nowrap"
            >
              📝 Đề xuất mới
            </button>
            <button
              type="button"
              className="text-[9px] sm:text-xs bg-gray-800/50 hover:bg-blue-600 hover:border-blue-500 border border-gray-700 text-gray-300 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full transition-all duration-200 shadow-sm whitespace-nowrap"
            >
              🔍 Tài liệu
            </button>
          </div>
        </div>

        {/* Input */}
        <div className="p-2 sm:p-3 border-t border-gray-800 bg-gray-900/80 rounded-b-2xl flex-shrink-0">
          <div className="relative flex items-center bg-gray-800/80 rounded-full border border-gray-700 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all duration-200 px-2 py-1">
            <button type="button" aria-label="Attach file" className="text-gray-400 hover:text-blue-400 p-1 sm:p-2 transition-colors rounded-full hover:bg-gray-700/50 flex items-center justify-center w-6 sm:w-8 h-6 sm:h-8 flex-shrink-0">
              <i className="fa-solid fa-paperclip text-xs sm:text-sm" />
            </button>
            <input
              type="text"
              placeholder="Nhập..."
              className="flex-1 bg-transparent border-none outline-none text-xs sm:text-sm text-gray-200 px-2 placeholder-gray-500 min-w-0"
            />
            <button type="button" aria-label="Send message" className="text-white bg-blue-600 hover:bg-blue-500 p-1 sm:p-2 transition-colors rounded-full w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center shadow-md flex-shrink-0">
              <i className="fa-solid fa-paper-plane text-xs" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}